import { useMemo } from 'react';

import type { QuestDependency, QuestEdgeType } from '../types';

const QUEST_COMMAND_TYPES: Record<string, QuestEdgeType> = {
  'RealWorld.Commands.RevealQuest': 'reveal',
  'RealWorld.Commands.UnlockQuest': 'unlock',
  'RealWorld.Commands.AccomplishQuest': 'accomplish',
  'RealWorld.Commands.LockQuest': 'lock'
};

const QUEST_DEPENDENCY_CONDITION_TYPE = 'RealWorld.Conditions.QuestAccomplished';

function normalizeNumericIds(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((value) => normalizeNumericIds(value));
  }

  if (typeof raw === 'object' && raw !== null) {
    if ('value' in raw) {
      return normalizeNumericIds((raw as { value?: unknown }).value);
    }
    return [];
  }

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return [raw];
  }

  return [];
}

interface AddCommandDependenciesParams {
  commands: ReactionCommand[] | undefined;
  quest: Quest;
  missionId: number;
  questToMission: Map<number, number>;
  dependencies: Map<string, QuestDependency>;
}

function addCommandDependencies({
  commands,
  quest,
  missionId,
  questToMission,
  dependencies
}: AddCommandDependenciesParams) {
  if (!commands) return;

  for (const command of commands) {
    const edgeType = QUEST_COMMAND_TYPES[command.type];
    if (edgeType) {
      const targetQuestIds = normalizeNumericIds(command.parameters?.questId);
      for (const targetQuestId of targetQuestIds) {
        const targetMissionId = questToMission.get(targetQuestId);
        if (targetMissionId && targetQuestId !== quest.id) {
          const key = `${quest.id}-${targetQuestId}-${edgeType}`;
          dependencies.set(key, {
            sourceQuestId: quest.id,
            sourceMissionId: missionId,
            targetQuestId,
            targetMissionId,
            edgeType
          });
        }
      }
    }
  }
}

function parseQuestDependencies(missions: Mission[]): QuestDependency[] {
  const dependencies = new Map<string, QuestDependency>();

  // Build questId → missionId lookup
  const questToMission = new Map<number, number>();
  for (const mission of missions) {
    if (mission.quests) {
      for (const quest of mission.quests) {
        questToMission.set(quest.id, mission.id);
      }
    }
  }

  for (const mission of missions) {
    if (mission.quests) {
      for (const quest of mission.quests) {
        const { actionHandler } = quest;
        if (actionHandler) {
          for (const reaction of actionHandler.reactionDefinitions ?? []) {
            if (reaction.conditions) {
              for (const condition of reaction.conditions) {
                if (condition.type === QUEST_DEPENDENCY_CONDITION_TYPE) {
                  const sourceQuestIds = normalizeNumericIds(condition.parameters?.questId);
                  for (const sourceQuestId of sourceQuestIds) {
                    const sourceMissionId = questToMission.get(sourceQuestId);
                    if (sourceMissionId && sourceQuestId !== quest.id) {
                      const key = `${sourceQuestId}-${quest.id}-accomplish`;
                      dependencies.set(key, {
                        sourceQuestId,
                        sourceMissionId,
                        targetQuestId: quest.id,
                        targetMissionId: mission.id,
                        edgeType: 'accomplish'
                      });
                    }
                  }
                }
              }
            }

            addCommandDependencies({
              commands: reaction.commands,
              quest,
              missionId: mission.id,
              questToMission,
              dependencies
            });
          }

          addCommandDependencies({
            commands: actionHandler.failureCommands,
            quest,
            missionId: mission.id,
            questToMission,
            dependencies
          });
        }
      }
    }
  }

  return [...dependencies.values()];
}

export default function useQuestDependencies(missions: Mission[] | undefined): QuestDependency[] {
  return useMemo(() => {
    if (!missions) return [];
    return parseQuestDependencies(missions);
  }, [missions]);
}
